import React, { useEffect, useState } from "react";
import SubledgerReconPanel from "@/pages/finance/SubledgerReconPanel";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState } from "@/components/app/ui-bits";
import { Scales, DownloadSimple, FilePdf, FileXls } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPE_LABEL = { asset: "Asset", liability: "Liability", equity: "Equity", revenue: "Revenue", expense: "Expense" };

function money(v) { return v ? formatCurrency(v) : "—"; }

function LedgerDrilldown({ code, filters, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!code) return;
    setData(null);
    api.get(`/finance/gl/accounts/${code}/ledger`, { params: filters })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={!!code} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{data ? `${data.account.code} · ${data.account.name}` : "Loading…"}</DialogTitle>
          <DialogDescription>
            {data ? `${data.lines.length} posted lines · DR ${formatCurrency(data.total_debit)} · CR ${formatCurrency(data.total_credit)} · closing ${formatCurrency(data.closing_balance)}` : ""}
          </DialogDescription>
        </DialogHeader>
        {data && (
          <div className="responsive-table-wrap max-h-[480px] overflow-y-auto" data-testid="tb-drilldown-lines">
            <table className="data-table w-full">
              <thead><tr><th>Date</th><th>Entry</th><th>Description</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th></tr></thead>
              <tbody>
                {data.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="font-mono-alt text-xs tabular-nums">{formatDate(l.date)}</td>
                    <td className="font-mono-alt text-xs tabular-nums">{l.entry_no}</td>
                    <td className="text-xs text-slate-600 max-w-[240px] truncate">{l.description || "—"}</td>
                    <td className="text-right font-mono-alt tabular-nums">{l.debit ? formatCurrency(l.debit) : "—"}</td>
                    <td className="text-right font-mono-alt tabular-nums">{l.credit ? formatCurrency(l.credit) : "—"}</td>
                    <td className="text-right font-mono-alt tabular-nums font-semibold">{formatCurrency(l.running_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function GLTrialBalanceTab() {
  const [tb, setTb] = useState(null);
  const [fromPeriod, setFromPeriod] = useState("");
  const [toPeriod, setToPeriod] = useState("");
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);

  const params = {};
  if (fromPeriod) params.from_period = fromPeriod;
  if (toPeriod) params.to_period = toPeriod;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance/gl/trial-balance", { params }); setTb(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (fmt) => {
    try {
      const res = await api.get(`/finance/gl/trial-balance/export.${fmt}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trial-balance.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Trial balance ${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!tb) return <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>;
  const t = tb.totals || {};
  const diff = Math.abs((t.closing_debit || 0) - (t.closing_credit || 0));

  return (
    <div className="space-y-4" data-testid="gl-trial-balance-tab">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="dc-card p-3">
          <div className="overline">Total debits</div>
          <div className="font-display text-xl font-bold text-slate-900 tabular-nums" data-testid="tb-total-debit">{formatCurrency(t.closing_debit || 0)}</div>
        </div>
        <div className="dc-card p-3">
          <div className="overline">Total credits</div>
          <div className="font-display text-xl font-bold text-slate-900 tabular-nums" data-testid="tb-total-credit">{formatCurrency(t.closing_credit || 0)}</div>
        </div>
        <div className="dc-card p-3">
          <div className="overline">Difference</div>
          <div className={`font-display text-xl font-bold tabular-nums ${diff <= 0.01 ? "text-slate-900" : "text-rose-600"}`}>{formatCurrency(diff)}</div>
        </div>
        <div className="dc-card p-3">
          <div className="overline">Ledger status</div>
          <div className={`font-display text-xl font-bold ${tb.balanced ? "text-emerald-600" : "text-rose-600"}`} data-testid="tb-balanced-flag">
            {tb.balanced ? "BALANCED ✓" : "NOT BALANCED"}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">From period</Label>
            <Input type="month" className="h-8 w-40" value={fromPeriod} onChange={(e) => setFromPeriod(e.target.value)} data-testid="tb-from-period" />
          </div>
          <div>
            <Label className="text-xs">To period</Label>
            <Input type="month" className="h-8 w-40" value={toPeriod} onChange={(e) => setToPeriod(e.target.value)} data-testid="tb-to-period" />
          </div>
          <Button size="sm" variant="outline" className="h-8" onClick={load} disabled={loading} data-testid="tb-apply-filters">Apply</Button>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => download("xlsx")} data-testid="tb-export-xlsx">
            <FileXls size={12} className="mr-1" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => download("pdf")} data-testid="tb-export-pdf">
            <FilePdf size={12} className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      {tb.rows.length === 0 ? (
        <EmptyState icon={Scales} title="No posted journal activity"
          description="Post journals or run the shadow-ledger migration (Journals tab) — the trial balance is generated exclusively from posted journal lines." />
      ) : (
        <div className="dc-card responsive-table-wrap">
          <table className="data-table w-full min-w-[1100px]">
            <thead>
              <tr>
                <th>Code</th><th>Account</th><th>Type</th>
                <th className="text-right">Opening DR</th><th className="text-right">Opening CR</th>
                <th className="text-right">Period DR</th><th className="text-right">Period CR</th>
                <th className="text-right">Closing DR</th><th className="text-right">Closing CR</th>
              </tr>
            </thead>
            <tbody>
              {tb.rows.map((r) => (
                <tr key={r.account_code} className="cursor-pointer hover:bg-slate-50/70 transition"
                  onClick={() => setDrill(r.account_code)} data-testid={`tb-row-${r.account_code}`}>
                  <td className="font-mono-alt text-xs tabular-nums">{r.account_code}</td>
                  <td className="font-semibold text-slate-900 text-xs">{r.account_name}</td>
                  <td className="text-[10px] uppercase tracking-wider text-slate-500">{TYPE_LABEL[r.account_type] || r.account_type}</td>
                  <td className="text-right font-mono-alt tabular-nums text-xs">{money(r.opening_debit)}</td>
                  <td className="text-right font-mono-alt tabular-nums text-xs">{money(r.opening_credit)}</td>
                  <td className="text-right font-mono-alt tabular-nums text-xs">{money(r.period_debit)}</td>
                  <td className="text-right font-mono-alt tabular-nums text-xs">{money(r.period_credit)}</td>
                  <td className="text-right font-mono-alt tabular-nums text-xs font-semibold">{money(r.closing_debit)}</td>
                  <td className="text-right font-mono-alt tabular-nums text-xs font-semibold">{money(r.closing_credit)}</td>
                </tr>
              ))}
              <tr className="font-bold border-t-2 border-slate-300 bg-slate-50" data-testid="tb-totals-row">
                <td /><td>TOTALS</td><td />
                <td className="text-right font-mono-alt tabular-nums text-xs">{money(t.opening_debit)}</td>
                <td className="text-right font-mono-alt tabular-nums text-xs">{money(t.opening_credit)}</td>
                <td className="text-right font-mono-alt tabular-nums text-xs">{money(t.period_debit)}</td>
                <td className="text-right font-mono-alt tabular-nums text-xs">{money(t.period_credit)}</td>
                <td className="text-right font-mono-alt tabular-nums text-xs">{money(t.closing_debit)}</td>
                <td className="text-right font-mono-alt tabular-nums text-xs">{money(t.closing_credit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[11px] text-slate-400 flex items-center gap-1">
        <DownloadSimple size={11} /> Click any account row to drill down into its posted journal lines.
      </div>
      <SubledgerReconPanel />

      <LedgerDrilldown code={drill} filters={params} onClose={() => setDrill(null)} />
    </div>
  );
}
